import {
  DocumentReaderReExtractState,
  DocumentReaderRunState,
  DocumentReaderWriteBackState,
  type IDocumentReaderReExtractProgress,
  type IDocumentReaderRunView,
  type IDocumentReaderWriteBackProgress,
} from '@commenda/api/commenda-ai/document-reader/types';
import type { JobProgressState } from '@commenda/pathfinder';
import type { UIMessage } from 'ai';

import { ChatCardHelper } from '../ChatCardHelper';
import {
  DocumentReadingRunHelper,
  type DocumentReadingStepKind,
} from './DocumentReadingRunHelper';
import {
  DocumentReadingTimeLeftHelper,
  type IDocumentReadingStepMeasure,
  type IDocumentReadingStepSample,
} from './DocumentReadingTimeLeftHelper';

/** What a docked strip follows: the reading itself, or a re-read or write-back of the finished run. */
export type DocumentReadingRunActivity = 'reading' | 'reRead' | 'writeBack';

/** Which of this chat's own tool results docked a run: the reading's start, a re-read, or a write-back. */
export type DocumentReadingRunDockSource = 'reading' | 'reRead' | 'writeBack';

/** One run the dock follows, and the tool result that docked it. */
export interface IDocumentReadingDockedRun {
  runId: string;
  source: DocumentReadingRunDockSource;
}

/** One run's docked strip, in the shape Pathfinder's JobProgress takes. */
export interface IDocumentReadingRunStripView {
  state: JobProgressState;
  title: string;
  detail: string | null;
  /** 0 to 100 while running; null for a step with no count yet, which spins the ring. */
  progress: number | null;
  progressLabel: string;
}

/** The strip above the composer: which runs it follows, what is moving on a run, and its text by step.
 * It never names a price or a cost. */
export class DocumentReadingRunStripHelper {
  /** The chat tool whose result names a reading run. */
  static readonly START_TOOL_NAME = 'start_document_reading';

  /** The chat tool that re-reads a finished run's documents; its input names the run. */
  static readonly REREAD_TOOL_NAME = 'start_document_rereading';

  /** The chat tool that writes a finished run's approved facts into Commenda; its input names the run. */
  static readonly WRITE_BACK_TOOL_NAME = 'start_document_write_back';

  /** The write-back states that mean a job is moving; every other answer started nothing. */
  private static readonly MOVING_WRITE_BACK_STATES: readonly string[] = [
    DocumentReaderWriteBackState.QUEUED,
    DocumentReaderWriteBackState.RUNNING,
  ];

  /** At most this many of a conversation's newest runs dock above the composer, newest on top. */
  static readonly MAX_DOCKED_RUNS = 2;

  /** How long a finished strip stays up before it tucks away. */
  static readonly SETTLED_STRIP_VISIBLE_MS = 4000;

  /** Each step's name in the strip, where it reads as a verb: "Read pages", "Stopped at Unzip". */
  static readonly STEP_NAMES: Readonly<
    Record<DocumentReadingStepKind, string>
  > = {
    unzip: 'Unzip',
    checkFiles: 'Check files',
    readPages: 'Read pages',
    understand: 'Understand documents',
    sort: 'Sort by company',
  };

  /** What a step counts, for a count whose total is not known yet: "212 files". */
  private static readonly COUNT_NOUNS: Readonly<
    Record<IDocumentReadingStepMeasure['key'], readonly [string, string]>
  > = {
    unzip: ['file', 'files'],
    checkFiles: ['file', 'files'],
    readPages: ['page', 'pages'],
    understand: ['document', 'documents'],
    sort: ['company', 'companies'],
    reRead: ['document', 'documents'],
  };

  private static readonly READING_TITLE = 'Reading your documents';

  private static readonly OUR_SIDE_ERROR = 'Something went wrong on our side';

  /** The runs this conversation started a job on, newest first and at most MAX_DOCKED_RUNS, read off
   * its own tool results only; the newest result on a run decides its source. */
  static getDockedRuns(
    messages: readonly UIMessage[]
  ): IDocumentReadingDockedRun[] {
    const newestFirst = messages
      .flatMap((message) => message.parts)
      .map((part) => DocumentReadingRunStripHelper.dockedRunOf(part))
      .filter((run): run is IDocumentReadingDockedRun => run !== null)
      .reverse();
    return newestFirst
      .filter(
        (run, index) =>
          newestFirst.findIndex((other) => other.runId === run.runId) === index
      )
      .slice(0, DocumentReadingRunStripHelper.MAX_DOCKED_RUNS);
  }

  /** Only a reading this session started gets the first-look "Finished reading" strip and chat refresh;
   * a run docked by a re-read or write-back, or already in the chat when it opened, does not. */
  static isNewInSession(
    run: Readonly<IDocumentReadingDockedRun>,
    runIdsAtOpen: readonly string[]
  ): boolean {
    return run.source === 'reading' && !runIdsAtOpen.includes(run.runId);
  }

  /** What is still moving on a run: the reading, or a re-read or write-back of the finished run. Null
   * once the run has ended and nothing more runs on it. */
  static getActivity(
    run: Readonly<IDocumentReaderRunView>
  ): DocumentReadingRunActivity | null {
    if (!DocumentReadingRunHelper.isFinished(run.state)) return 'reading';
    if (run.reExtract?.state === DocumentReaderReExtractState.RUNNING)
      return 'reRead';
    if (
      run.writeBack &&
      !DocumentReadingRunHelper.isWriteBackFinished(run.writeBack.state)
    )
      return 'writeBack';
    return null;
  }

  /** The strip for what the run is doing, or last did: "<Step> · <percent>% · <count> of <total> <unit> ·
   * About N min left", with the ring on the current step's own count. `now` is when this run view was
   * read, so the time left stays a pure function of its inputs. */
  static getStripView(
    run: Readonly<IDocumentReaderRunView>,
    activity: DocumentReadingRunActivity,
    sample: Readonly<IDocumentReadingStepSample> | null,
    now: number
  ): IDocumentReadingRunStripView {
    const measure = DocumentReadingTimeLeftHelper.getStepMeasure(run);
    const timeLeft = DocumentReadingTimeLeftHelper.getTimeLeftText(
      measure,
      sample,
      now,
      measure?.key === 'readPages' ? run.readingSecondsLeft : null
    );
    if (activity === 'reRead' && run.reExtract)
      return DocumentReadingRunStripHelper.getReReadStripView(
        run.reExtract,
        measure,
        timeLeft
      );
    if (activity === 'writeBack' && run.writeBack)
      return DocumentReadingRunStripHelper.getWriteBackStripView(run.writeBack);
    return DocumentReadingRunStripHelper.getReadingStripView(
      run,
      measure,
      timeLeft
    );
  }

  /** "61%" for a step measured across its stages, "34% · 117 of 371 files" with a total, "212 files"
   * without one, null without a count. */
  static getCountText(
    measure: Readonly<IDocumentReadingStepMeasure> | null
  ): string | null {
    if (!measure) return null;
    const percent = DocumentReadingRunStripHelper.getStepPercent(measure);
    if (DocumentReadingTimeLeftHelper.isShareMeasure(measure))
      return `${percent ?? 0}%`;
    const [singular, plural] =
      DocumentReadingRunStripHelper.COUNT_NOUNS[measure.key];
    const countOf = DocumentReadingRunHelper.formatCountOf;
    if (measure.total !== null)
      return `${percent ?? 0}% · ${DocumentReadingRunHelper.formatCount(measure.done)} of ${countOf(measure.total, singular, plural)}`;
    return countOf(measure.done, singular, plural);
  }

  /** The step's own share done, 0 to 100, and 100 only once every item is done, so the strip never says
   * "100% · 370 of 371"; null without a total, which spins the ring. */
  static getStepPercent(
    measure: Readonly<IDocumentReadingStepMeasure> | null
  ): number | null {
    if (!measure || measure.total === null) return null;
    if (measure.done >= measure.total) return 100;
    return Math.min(99, Math.round((measure.done / measure.total) * 100));
  }

  private static getReadingStripView(
    run: Readonly<IDocumentReaderRunView>,
    measure: Readonly<IDocumentReadingStepMeasure> | null,
    timeLeft: string | null
  ): IDocumentReadingRunStripView {
    const toStrip = DocumentReadingRunStripHelper.toStripView;
    const title = DocumentReadingRunStripHelper.READING_TITLE;
    const step = DocumentReadingRunHelper.getStep(run.stage);
    const stepName = step
      ? DocumentReadingRunStripHelper.STEP_NAMES[step.kind]
      : null;
    // A run only starts on a finished upload, so a queued run is waiting for a worker, not for files.
    if (run.state === DocumentReaderRunState.QUEUED)
      return toStrip('queued', title, 'Starting', null);
    if (run.state === DocumentReaderRunState.AWAITING_APPROVAL)
      return toStrip('awaiting', title, 'Waiting for your approval', null);
    if (run.state === DocumentReaderRunState.COMPLETED)
      return DocumentReadingRunStripHelper.getDoneStripView(run);
    if (
      run.state === DocumentReaderRunState.STOPPED ||
      run.state === DocumentReaderRunState.FAILED
    )
      return toStrip(
        'failed',
        "Couldn't finish reading",
        DocumentReadingRunStripHelper.joinParts([
          stepName ? `Stopped at ${stepName}` : null,
          DocumentReadingRunStripHelper.OUR_SIDE_ERROR,
        ]),
        null
      );

    return toStrip(
      'running',
      title,
      DocumentReadingRunStripHelper.joinParts([
        stepName,
        DocumentReadingRunStripHelper.getCountText(measure),
        timeLeft,
      ]),
      DocumentReadingRunStripHelper.getStepPercent(measure)
    );
  }

  /** "Done in 48 min · 678 documents", or the files it could not read when there were any. */
  private static getDoneStripView(
    run: Readonly<IDocumentReaderRunView>
  ): IDocumentReadingRunStripView {
    const title = 'Finished reading your documents';
    const countOf = DocumentReadingRunHelper.formatCountOf;
    const { filesFailed, filesNotSupported, documents } = run.counters;
    const unreadFiles = filesFailed + filesNotSupported;
    const startedAt = run.startedAt ? Date.parse(run.startedAt) : NaN;
    const finishedAt = run.finishedAt ? Date.parse(run.finishedAt) : NaN;
    const elapsedMs = finishedAt - startedAt;
    const doneIn = Number.isFinite(elapsedMs)
      ? `Done in ${elapsedMs < 60_000 ? 'under a minute' : DocumentReadingTimeLeftHelper.formatMinutes(Math.round(elapsedMs / 60_000))}`
      : null;
    if (unreadFiles > 0)
      return DocumentReadingRunStripHelper.toStripView(
        'issues',
        title,
        DocumentReadingRunStripHelper.joinParts([
          doneIn,
          `${countOf(unreadFiles, 'file', 'files')} couldn't be read`,
        ]),
        null
      );
    return DocumentReadingRunStripHelper.toStripView(
      'done',
      title,
      DocumentReadingRunStripHelper.joinParts([
        doneIn,
        documents > 0 ? countOf(documents, 'document', 'documents') : null,
      ]),
      null
    );
  }

  private static getReReadStripView(
    reExtract: Readonly<IDocumentReaderReExtractProgress>,
    measure: Readonly<IDocumentReadingStepMeasure> | null,
    timeLeft: string | null
  ): IDocumentReadingRunStripView {
    const toStrip = DocumentReadingRunStripHelper.toStripView;
    const countOf = DocumentReadingRunHelper.formatCountOf;
    const { state, documentsToRedo, papersDone } = reExtract;
    const hasCounts = papersDone !== undefined && documentsToRedo > 0;
    const documentsPart = hasCounts
      ? `${DocumentReadingRunHelper.formatCount(Math.min(papersDone, documentsToRedo))} of ${countOf(documentsToRedo, 'document', 'documents')}`
      : null;
    if (state === DocumentReaderReExtractState.RUNNING)
      return toStrip(
        'running',
        'Re-reading the documents',
        DocumentReadingRunStripHelper.joinParts([
          DocumentReadingRunStripHelper.getCountText(measure),
          timeLeft,
        ]),
        DocumentReadingRunStripHelper.getStepPercent(measure)
      );
    if (state === DocumentReaderReExtractState.DONE)
      return toStrip(
        'done',
        'Finished re-reading the documents',
        documentsToRedo > 0
          ? `${countOf(documentsToRedo, 'document', 'documents')} re-read`
          : null,
        null
      );
    return toStrip(
      'failed',
      "Couldn't finish re-reading",
      DocumentReadingRunStripHelper.joinParts([
        documentsPart ? `Stopped after ${documentsPart}` : null,
        DocumentReadingRunStripHelper.OUR_SIDE_ERROR,
      ]),
      null
    );
  }

  /** The company being written, its items and its place among the companies, with the ring on the
   * current company's items; the counts once done. */
  private static getWriteBackStripView(
    writeBack: Readonly<IDocumentReaderWriteBackProgress>
  ): IDocumentReadingRunStripView {
    const toStrip = DocumentReadingRunStripHelper.toStripView;
    const { joinParts } = DocumentReadingRunStripHelper;
    const count = DocumentReadingRunHelper.formatCount;
    const countOf = DocumentReadingRunHelper.formatCountOf;
    const { state, total, done, currentCompany, itemsDone, itemsTotal } =
      writeBack;
    if (state === DocumentReaderWriteBackState.DONE) {
      const { created, updated, needsInfo, clashes } = writeBack;
      return toStrip(
        needsInfo + clashes > 0 ? 'issues' : 'done',
        'Finished writing into Commenda',
        joinParts([
          created > 0 ? `${count(created)} created` : null,
          updated > 0 ? `${count(updated)} updated` : null,
          needsInfo > 0
            ? `${count(needsInfo)} ${needsInfo === 1 ? 'needs' : 'need'} info`
            : null,
          clashes > 0
            ? `${countOf(clashes, 'difference', 'differences')} to check`
            : null,
        ]),
        null
      );
    }
    if (state === DocumentReaderWriteBackState.FAILED)
      return toStrip(
        'failed',
        "Couldn't finish writing into Commenda",
        joinParts([
          total > 0
            ? `Stopped after ${count(done)} of ${countOf(total, 'company', 'companies')}`
            : null,
          DocumentReadingRunStripHelper.OUR_SIDE_ERROR,
        ]),
        null
      );

    const hasItems =
      !!currentCompany && itemsDone !== undefined && !!itemsTotal;
    const companyPart = currentCompany
      ? `company ${count(Math.min(done + 1, total))} of ${count(total)}`
      : `${count(done)} of ${countOf(total, 'company', 'companies')}`;
    return toStrip(
      state === DocumentReaderWriteBackState.QUEUED ? 'queued' : 'running',
      'Writing into Commenda',
      joinParts([
        currentCompany ?? null,
        hasItems
          ? `${count(Math.min(itemsDone, itemsTotal))} of ${countOf(itemsTotal, 'item', 'items')}`
          : null,
        total > 0 ? companyPart : null,
      ]),
      hasItems
        ? Math.min(100, Math.round((itemsDone / itemsTotal) * 100))
        : null
    );
  }

  /** The run one of the three start tools' results docks; null for every other part. Matched by name
   * first, so a long chat is not parsed part by part on every streamed token. */
  private static dockedRunOf(
    part: UIMessage['parts'][number]
  ): IDocumentReadingDockedRun | null {
    const { START_TOOL_NAME, REREAD_TOOL_NAME, WRITE_BACK_TOOL_NAME } =
      DocumentReadingRunStripHelper;
    const partToolName =
      part.type === 'dynamic-tool'
        ? part.toolName
        : part.type.startsWith('tool-') && part.type.slice('tool-'.length);
    if (!partToolName) return null;

    if (partToolName === START_TOOL_NAME) {
      const card = ChatCardHelper.forPart(part);
      return card?.kind === 'documentReading'
        ? { runId: card.runId, source: 'reading' }
        : null;
    }
    if (
      partToolName !== REREAD_TOOL_NAME &&
      partToolName !== WRITE_BACK_TOOL_NAME
    )
      return null;

    const result = ChatCardHelper.toolResultOf(part);
    const runId = result?.input?.runId;
    if (!result || typeof runId !== 'string' || runId.trim() === '')
      return null;
    if (partToolName === REREAD_TOOL_NAME)
      return result.payload.started === true
        ? { runId, source: 'reRead' }
        : null;
    const { state } = result.payload;
    return typeof state === 'string' &&
      DocumentReadingRunStripHelper.MOVING_WRITE_BACK_STATES.includes(state)
      ? { runId, source: 'writeBack' }
      : null;
  }

  private static toStripView(
    state: JobProgressState,
    title: string,
    detail: string | null,
    progress: number | null
  ): IDocumentReadingRunStripView {
    return {
      state,
      title,
      detail,
      progress,
      progressLabel: progress === null ? title : `${title}, ${progress}%`,
    };
  }

  private static joinParts(parts: readonly (string | null)[]): string | null {
    const kept = parts.filter((part): part is string => !!part);
    return kept.length > 0 ? kept.join(' · ') : null;
  }
}
